import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { authApi, workspacesApi } from '../api-client/index'
import type { CreateWorkspaceInput, Workspace, WorkspaceRole } from '../types/index'
import { useAuthStore } from './auth'

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaces = ref<Workspace[]>([])
  const activeId = ref<string | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const active = computed<Workspace | null>(() =>
    workspaces.value.find((w) => w.id === activeId.value) ?? null,
  )
  const isBusinessMode = computed(() => active.value?.kind === 'business')
  const myRole = computed<WorkspaceRole | null>(() => active.value?.myRole ?? null)
  const canManageMembers = computed(() =>
    myRole.value === 'owner' || myRole.value === 'admin',
  )

  async function load(): Promise<Workspace[]> {
    loading.value = true
    try {
      const list = await workspacesApi.list()
      workspaces.value = list
      if (!activeId.value) {
        const auth = useAuthStore()
        const claim = auth.user?.workspaceId
        activeId.value =
          claim && list.some((w) => w.id === claim) ? claim : list[0]?.id ?? null
      }
      return list
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function create(input: CreateWorkspaceInput): Promise<Workspace> {
    const ws = await workspacesApi.create(input)
    workspaces.value = [ws, ...workspaces.value]
    return ws
  }

  async function switchTo(id: string): Promise<void> {
    const { accessToken } = await authApi.switchWorkspace(id)
    const auth = useAuthStore()
    auth.accessToken = accessToken
    activeId.value = id
    await auth.refreshSelf()
  }

  function clear() {
    workspaces.value = []
    activeId.value = null
    initialized.value = false
  }

  return {
    workspaces,
    activeId,
    active,
    isBusinessMode,
    myRole,
    canManageMembers,
    loading,
    initialized,
    load,
    create,
    switchTo,
    clear,
  }
})
